import {
  DOCUMENT,
  Directive,
  ElementRef,
  OnDestroy,
  PLATFORM_ID,
  afterNextRender,
  inject,
  input,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';

/**
 * The page's one entrance.
 *
 * Nocturne specifies no motion at all: no transitions, no animations, no
 * keyframes, no hover or focus states anywhere in the design canvas. That is a
 * gap rather than a prohibition, so this site invents a motion language and
 * confines itself to it:
 *
 *     Nothing moves that the reader did not scroll to,
 *     and everything that moves, moves once.
 *
 * No loops, no parallax, no spinners. The element carries `.reveal`,
 * `.reveal-rule` or `.reveal-fill` from `styles.css`; this directive adds
 * `.is-in` the first time the element intersects the viewport, then stops
 * observing it. There is no exit state and no re-trigger: scrolling back up
 * and down again does not replay anything, because a thing that animates every
 * time you pass it is decoration, and this system does not decorate.
 *
 * Server-side this is inert and the element is left in its finished state, so
 * the prerendered HTML is the complete page: a reader without JavaScript, or
 * one whose observer never fires, sees everything.
 */
@Directive({
  selector: '[appReveal]',
  standalone: true,
  host: {
    '[style.--reveal-delay]': 'delayMs()',
  },
})
export class RevealDirective implements OnDestroy {
  /**
   * Stagger, in milliseconds. Kept small and used sparingly: a stagger long
   * enough to notice reads as a sequence, and the content is not a sequence.
   */
  readonly appReveal = input<number | ''>('');

  private readonly element = inject(ElementRef<HTMLElement>);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);

  private observer: IntersectionObserver | null = null;

  constructor() {
    // On the server the class simply ships applied: prerendered HTML is the
    // finished page, never the pre-animation state.
    if (isPlatformServer(this.platformId)) {
      this.element.nativeElement.classList.add('is-in');
      return;
    }

    afterNextRender(() => this.observe());
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  protected delayMs(): string {
    const value = this.appReveal();
    return typeof value === 'number' && value > 0 ? `${value}ms` : '0ms';
  }

  private observe(): void {
    const node = this.element.nativeElement as HTMLElement;

    // A reader who has asked for less motion gets the finished state
    // immediately. The CSS says the same thing; this keeps the observer from
    // being created at all.
    const reducedMotion = this.document.defaultView?.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (reducedMotion || typeof IntersectionObserver === 'undefined') {
      node.classList.add('is-in');
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-in');
          this.observer?.unobserve(entry.target);
        }
      },
      {
        // Fire a little before the element is fully in view, so the motion has
        // finished by the time the reader's eye arrives. An entrance the
        // reader watches happen is an entrance they are waiting on.
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.01,
      }
    );

    this.observer.observe(node);
  }
}
